// src/app/customer-quotes.jsx
import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity } from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IoCalendarOutline } from 'react-icons/io5';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

export default function CustomerQuotes() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');
  const customerName = searchParams.get('customerName') || 'Customer';

  const [quotes, setQuotes] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);

  const PAGE_SIZE = 25;

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          const user_type = parsedPermissions?.user_type;
          const quotePerm = parsedPermissions?.quote_perm;

          // Block access if user is NOT (Owner OR Admin) AND has no invoice permission
          if ((user_type !== 'owner' && user_type !== 'admin') && quotePerm === 'none') {
            alert("You don't have permission to view quotes. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  const loadQuotes = async (nextPage = 1) => {
    if (!customerId) return;
    try {
      setLoading(true);
      const token = getToken();
      const { data } = await axios.get(
        `${API_BASE_URL}/api/quotes?customerId=${encodeURIComponent(customerId)}&page=${nextPage}&page_size=${PAGE_SIZE}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      const list = Array.isArray(data) ? data : data?.quotes ?? [];

      setQuotes((prev) => (nextPage === 1 ? list : [...prev, ...list]));
      setHasMore(list.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      alert('Failed to load quotes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // reset when customer changes
    setQuotes([]);
    setHasMore(true);
    setPage(1);
    loadQuotes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleLoadMore = () => {
    if (!hasMore || loading) return;
    const next = page + 1;
    setPage(next);
    loadQuotes(next);
  };

  const formatDate = (selectedDate, selectedTime) => {
    if (!selectedDate || !selectedTime) return '';
    const d = new Date(`${selectedDate}T${selectedTime}Z`);
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (selectedDate, selectedTime) => {
    if (!selectedDate || !selectedTime) return '';
    const d = new Date(`${selectedDate}T${selectedTime}Z`);
    const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return `${date} at ${time}`;
  };

  // Helper function to format created date
  const formatCreatedDate = (created) => {
    if (!created) return 'Invalid Date';
    const d = new Date(created);
    return d.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  // Helper function to format edited date (only if not default)
  const formatEditedDate = (edited) => {
    if (!edited) return null;
    
    const editedDate = new Date(edited);
    
    // Check if edited date is the default (1970-01-01)
    // Compare year, month, and day to avoid timezone issues
    if (editedDate.getFullYear() === 1970 && 
        editedDate.getMonth() === 0 && 
        editedDate.getDate() === 1) {
      return null;
    }
    
    // Convert to local time and format as date only
    return editedDate.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatSentDate = (dateSent) => {
    if (!dateSent) return null;
    const dateString = typeof dateSent === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateSent)
      ? `${dateSent}Z`
      : dateSent;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (!customerId) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>No customer selected.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.header}>{`${customerName || 'Customer'}'s quotes`}</Text>

      {quotes.length > 0 ? (
        <FlatList
          data={quotes}
          keyExtractor={(item, index) => String(item.id ?? index)}
          renderItem={({ item }) => {
            const createdDate = formatCreatedDate(item.created);
            const editedDate = formatEditedDate(item.edited);
            const sentDate = item.sentEmail === 'yes' ? formatSentDate(item.dateSent) : null;

            return (
              <TouchableOpacity
                style={styles.card}
                onPress={() => navigate(`/quote-details?id=${encodeURIComponent(item.id)}`)}
              >
                <View style={styles.cardHeader}>
                  <Text style={styles.quoteTitle}>{item.title}</Text>
                  <View style={styles.dateContainer}>
                    <Text style={styles.dateText}>{`#${item.id}`} </Text>
                    <IoCalendarOutline size={16} color="green" style={{ marginHorizontal: 8 }} aria-hidden />
                    <Text style={styles.dateText}>{createdDate}</Text>
                  </View>
                </View>
                <Text style={styles.quoteDetail}>{`Description: ${item.description || 'No description'}`}</Text>
                {canSeePrices() && <Text style={styles.quoteDetail}>{`Price: $${item.price}`}</Text>}
                <Text style={styles.quoteDetail}>
                  {`Sent: ${
                    item.sentEmail === 'yes' && item.sentText === 'yes'
                      ? 'Email, Text'
                      : item.sentEmail === 'yes'
                      ? 'Email'
                      : item.sentText === 'yes'
                      ? 'Text'
                      : 'No'
                  }${sentDate ? ` - ${sentDate}` : ''}`}
                </Text>
                {editedDate && (
                  <Text style={styles.quoteDetail}>{`Edited: ${editedDate}`}</Text>
                )}
              </TouchableOpacity>
            );
          }}
          ListFooterComponent={
            hasMore ? (
              <TouchableOpacity
                style={styles.loadMoreButton}
                onPress={handleLoadMore}
                disabled={loading}
              >
                <Text style={styles.loadMoreText}>{loading ? 'Loading...' : 'Load More'}</Text>
              </TouchableOpacity>
            ) : null
          }
        />
      ) : (
        <Text style={styles.noQuotesText}>No quotes found for this customer.</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, paddingBottom: 50, backgroundColor: '#f9f9f9' },
  header: { 
    fontSize: 24, 
    fontWeight: 'bold', 
    marginBottom: 15, 
    textAlign: 'center',
    textDecorationLine: 'underline',
    color: '#333',
  },
  card: {
    backgroundColor: '#f0f0f0',
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  dateContainer: { flexDirection: 'row', alignItems: 'center' },
  dateText: { marginLeft: 5, fontSize: 14, color: 'green', fontWeight: 'bold' },
  quoteTitle: { fontSize: 18, fontWeight: 'bold', color: '#333', textDecorationLine: 'underline' }, 
  quoteDetail: { fontSize: 14, color: '#444', marginBottom: 4 },
  noQuotesText: { textAlign: 'center', color: '#999', fontSize: 16, marginTop: 20 },
  loadMoreButton: {
    padding: 10,
    backgroundColor: '#007AFF',
    borderRadius: 5,
    alignItems: 'center',
    marginVertical: 10,
  },
  loadMoreText: {
    color: '#fff',
    fontSize: 16,
  },
});
